import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const kit = {
  name: 'VeraBIND Tau',
  image: '/kit_0001.png',
  imageAlt: 'VeraBIND Tau kit',
  description:
    'First-in-class, blood-based assay quantifying hyperphosphorylated tau for early Alzheimer\'s detection; verified against tau-PET with over 96% agreement.',
};

const person = {
  firstName: 'Jane',
  lastName: 'Smith',
  dob: 'Jan 12, 1968',
  phone: '(555) 123-4567',
  emailOk: 'Yes',
};

const shipping = {
  line1: '123 Oak Street',
  line2: 'Apt 4B',
  city: 'San Diego',
  state: 'CA',
  zip: '92101',
};

const provider = {
  name: 'Dr. Lauren Martinez, MD',
  addressLine1: '4400 W. Research Parkway',
  addressLine2: 'Suite 210, San Diego, CA 92121',
};

const productCost = '$1,495';

export default function CustomerKitPurchaseConfirmation() {
  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [personForm, setPersonForm] = useState(person);
  const [shippingForm, setShippingForm] = useState(shipping);

  const handlePersonSave = () => {
    setPersonDialogOpen(false);
  };

  const handleShippingSave = () => {
    setShippingDialogOpen(false);
  };

  const handlePaymentClose = () => {
    setPaymentDialogOpen(false);
  };

  return (
    <Container disableGutters sx={{ mt: 0 }}>
      <Box
        sx={{
          maxWidth: 'md',
          mx: 'auto',
          textAlign: 'left',
          pt: { xs: 0.5, md: 0.5 },
          pb: { xs: 0.5, md: 0.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Card
          sx={{
            border: '1px solid #97999B',
            borderRadius: 2,
            boxShadow: '0 18px 40px rgba(0, 0, 0, 0.08)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Typography variant="h4" component="h1">
              Confirmation and Payment
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Review your kit and details below before continuing to payment.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.4fr 0.9fr' },
                gap: 2,
                mt: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Person
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Edit person"
                        onClick={() => setPersonDialogOpen(true)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {personForm.firstName} {personForm.lastName}
                    </Typography>
                    <Typography variant="body2">DOB: {personForm.dob}</Typography>
                    <Typography variant="body2">Cell: {personForm.phone}</Typography>
                    <Typography variant="body2">Email updates: {personForm.emailOk}</Typography>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Shipping Address
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Edit shipping address"
                        onClick={() => setShippingDialogOpen(true)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {shippingForm.line1}
                    </Typography>
                    <Typography variant="body2">{shippingForm.line2}</Typography>
                    <Typography variant="body2">
                      {shippingForm.city}, {shippingForm.state} {shippingForm.zip}
                    </Typography>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Assigned Provider
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {provider.name}
                    </Typography>
                    <Typography variant="body2">{provider.addressLine1}</Typography>
                    <Typography variant="body2">{provider.addressLine2}</Typography>
                  </CardContent>
                </Card>
              </Box>

              <Card
                sx={{
                  border: '1px solid #97999B',
                  borderRadius: 2,
                  boxShadow: 'none',
                  alignSelf: 'start',
                }}
              >
                <CardMedia
                  component="img"
                  image={kit.image}
                  alt={kit.imageAlt}
                  sx={{
                    width: '100%',
                    height: 160,
                    objectFit: 'cover',
                    borderBottom: '1px solid #97999B',
                  }}
                />
                <CardContent>
                  <Typography variant="subtitle2" component="p">
                    Selected kit
                  </Typography>
                  <Typography variant="h6" component="h2" sx={{ mt: 0.5 }}>
                    {kit.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {kit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total: {productCost}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setPaymentDialogOpen(true)}
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  boxShadow: '0 12px 22px rgba(0, 118, 127, 0.28)',
                  py: 1.1,
                  px: 4,
                }}
              >
                Purchase
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={personDialogOpen} onClose={() => setPersonDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Person</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, overflow: 'visible' }}>
          <TextField
            label="First name"
            value={personForm.firstName}
            onChange={(event) => setPersonForm({ ...personForm, firstName: event.target.value })}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
          <TextField
            label="Last name"
            value={personForm.lastName}
            onChange={(event) => setPersonForm({ ...personForm, lastName: event.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Date of birth"
            value={personForm.dob}
            onChange={(event) => setPersonForm({ ...personForm, dob: event.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Cell phone"
            value={personForm.phone}
            onChange={(event) => setPersonForm({ ...personForm, phone: event.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Email updates"
            value={personForm.emailOk}
            onChange={(event) => setPersonForm({ ...personForm, emailOk: event.target.value })}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePersonSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shippingDialogOpen} onClose={() => setShippingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Shipping Address</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, overflow: 'visible' }}>
          <TextField
            label="Address line 1"
            value={shippingForm.line1}
            onChange={(event) => setShippingForm({ ...shippingForm, line1: event.target.value })}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
          <TextField
            label="Address line 2"
            value={shippingForm.line2}
            onChange={(event) => setShippingForm({ ...shippingForm, line2: event.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="City"
            value={shippingForm.city}
            onChange={(event) => setShippingForm({ ...shippingForm, city: event.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="State"
            value={shippingForm.state}
            onChange={(event) => setShippingForm({ ...shippingForm, state: event.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="ZIP code"
            value={shippingForm.zip}
            onChange={(event) => setShippingForm({ ...shippingForm, zip: event.target.value })}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShippingDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleShippingSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={handlePaymentClose} maxWidth="xs" fullWidth>
        <DialogTitle>Secure payment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2">
            This is a mock Stripe checkout. No real payment will be processed.
          </Typography>
          <TextField label="Card number" placeholder="4242 4242 4242 4242" fullWidth size="small" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField label="MM / YY" placeholder="12 / 34" fullWidth size="small" />
            <TextField label="CVC" placeholder="123" fullWidth size="small" />
          </Box>
          <TextField label="Name on card" placeholder="Jane Smith" fullWidth size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentClose}>Cancel</Button>
          <Button
            variant="contained"
            component={RouterLink}
            to="/mock/customer-purchase-complete"
            onClick={handlePaymentClose}
          >
            Pay {productCost}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
